<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import validators from '@/utils/validators.ts';
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import UserService from '@/services/user/userService.ts';

import type { ValidationRule } from '@/types/utils/validators.type.ts';
import type { UpdateUserRequest, User } from '@/types/services/user/user.type.ts';

const props = defineProps<{
  userId: number
  drawerMode?: boolean
}>()

const emit = defineEmits<{
  (emit: 'updated'): void
}>()

const { t } = useI18n()
const snackbarStore = useSnackbarStore()

const loading = ref<boolean>(false)

//--- Update user form ---//
const formValid = ref<boolean>(false)
const emailRules = ref<ValidationRule[]>([...validators.emailRules])
const firstNameRules = ref<ValidationRule[]>([...validators.firstNameRules])
const lastNameRules = ref<ValidationRule[]>([...validators.lastNameRules])
const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  role: '',
})

watch(
  () => props.userId,
  (userId) => {
    getUser(userId)
  },
  { immediate: true }
)

async function getUser(userId: number): Promise<void> {
  try {
    loading.value = true;

    const response: User = await UserService.getDam(userId);

    if (response) {
      form.firstName = response.firstName
      form.lastName = response.lastName
      form.email = response.email
      form.role = response.role
    }
  } finally {
    loading.value = false;
  }
}

async function updateUser(): Promise<void> {
  loading.value = true

  const user: UpdateUserRequest = {
    firstName: form.firstName,
    lastName: form.lastName
  }

  try {
    await UserService.updateDam(props.userId, user)

    snackbarStore.openSnackbar({
      text: t("views.updateDam.updateSuccess"),
      color: "success"
    })
  } finally {
    loading.value = false
    emit('updated')
  }
}
</script>

<template>
  <!-- User creation form -->
  <v-row>
    <v-col cols="12">
      <v-form v-model="formValid" @submit.prevent="updateUser">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <v-icon class="mr-2" color="primary" icon="PhInfo" />
            <h2 class="subtitle">{{ t("views.updateDam.subtitle") }}</h2>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.firstName"
              :label="t('common.inputs.firstName.label')"
              :placeholder="t('common.inputs.firstName.placeholder')"
              :rules="firstNameRules"
              autocomplete="given-name"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.lastName"
              :label="t('common.inputs.lastName.label')"
              :placeholder="t('common.inputs.lastName.placeholder')"
              :rules="lastNameRules"
              autocomplete="family-name"
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.email"
              :label="t('common.inputs.email.label')"
              :placeholder="t('common.inputs.email.placeholder')"
              :rules="emailRules"
              autocomplete="email"
              hide-details="auto"
              disabled
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field
              :model-value="t(`common.role.${form.role}`)"
              :label="t('common.inputs.role.label')"
              autocomplete="off"
              hide-details="auto"
              disabled
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn
              :disabled="!formValid || loading"
              :loading="loading"
              :text="t('common.buttons.update')"
              class="w-100 w-sm-auto"
              type="submit"
            >
              <template #prepend>
                <v-icon icon="PhUserCheck"/>
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>
